import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Button,
	ButtonProps,
	Dialog,
	DialogActions,
	DialogActionsProps,
	DialogContent,
	DialogContentProps,
	DialogProps,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useAppSocket } from "../providers/AppSocketContext";
import { Z_INDEX } from "../styles/zIndex";

export const useMoleThemeColor = (override?: string) => {
	const theme = useTheme();
	const { appSettings } = useAppSocket();
	return override || appSettings?.UI_THEME_PRIMARY?.value || theme.palette.primary.main;
};

export const moleDialogPaperSx = (
	themeColor: string,
	options?: { maxHeight?: string; width?: string; maxWidth?: string },
) => ({
	borderRadius: 3,
	borderTop: `3px solid ${themeColor}`,
	overflow: "hidden",
	backgroundImage: "none",
	...(options?.maxHeight ? { maxHeight: options.maxHeight } : {}),
	...(options?.width ? { width: options.width } : {}),
	...(options?.maxWidth ? { maxWidth: options.maxWidth } : {}),
});

interface MoleDialogProps extends Omit<DialogProps, "PaperProps"> {
	themeColor?: string;
	paperOptions?: { maxHeight?: string; width?: string; maxWidth?: string };
	PaperProps?: DialogProps["PaperProps"];
}

export const MoleDialog = ({
	themeColor: themeColorProp,
	paperOptions,
	sx,
	PaperProps,
	children,
	...props
}: MoleDialogProps) => {
	const themeColor = useMoleThemeColor(themeColorProp);

	return (
		<Dialog
			{...props}
			sx={{ zIndex: Z_INDEX.dialog, ...sx }}
			PaperProps={{
				...PaperProps,
				sx: {
					...moleDialogPaperSx(themeColor, paperOptions),
					...(PaperProps?.sx as object),
				},
			}}
		>
			{children}
		</Dialog>
	);
};

interface MoleDialogHeaderProps {
	title: ReactNode;
	subtitle?: ReactNode;
	eyebrow?: ReactNode;
	icon?: ReactNode;
	onClose?: () => void;
	closeLabel?: string;
	themeColor?: string;
	extra?: ReactNode;
}

export const MoleDialogHeader = ({
	title,
	subtitle,
	eyebrow,
	icon,
	onClose,
	closeLabel,
	themeColor: themeColorProp,
	extra,
}: MoleDialogHeaderProps) => {
	const themeColor = useMoleThemeColor(themeColorProp);
	const { t } = useTranslation();

	return (
		<DialogTitle
			sx={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "flex-start",
				gap: 1,
				background: `linear-gradient(180deg, ${alpha(themeColor, 0.07)} 0%, transparent 100%)`,
			}}
		>
			<Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
				{icon && (
					<Box
						sx={{
							width: 42,
							height: 42,
							borderRadius: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: alpha(themeColor, 0.12),
							color: themeColor,
							flexShrink: 0,
						}}
					>
						{icon}
					</Box>
				)}
				<Box sx={{ minWidth: 0 }}>
					{eyebrow && (
						<Typography
							variant="caption"
							sx={{
								fontWeight: 800,
								color: themeColor,
								textTransform: "uppercase",
								letterSpacing: 1,
								display: "block",
								lineHeight: 1.2,
							}}
						>
							{eyebrow}
						</Typography>
					)}
					<Typography variant="h6" fontWeight={700} color={themeColor} noWrap={false}>
						{title}
					</Typography>
					{subtitle && (
						<Typography variant="body2" color="text.secondary">
							{subtitle}
						</Typography>
					)}
				</Box>
				{extra && <Box sx={{ ml: "auto", flexShrink: 0 }}>{extra}</Box>}
			</Stack>
			{onClose && (
				<IconButton onClick={onClose} aria-label={closeLabel ?? t("common:ui.buttons.close")} sx={{ flexShrink: 0, mt: -0.5 }}>
					<CloseIcon />
				</IconButton>
			)}
		</DialogTitle>
	);
};

export const MoleDialogContent = ({ sx, ...props }: DialogContentProps) => (
	<DialogContent sx={{ pt: 1, ...sx }} {...props} />
);

interface MoleDialogActionsProps extends DialogActionsProps {
	leading?: ReactNode;
}

export const MoleDialogActions = ({ leading, children, sx, ...props }: MoleDialogActionsProps) => {
	const theme = useTheme();

	return (
		<DialogActions
			{...props}
			sx={{
				px: 3,
				py: 2,
				borderTop: 1,
				borderColor: "divider",
				bgcolor: alpha(theme.palette.background.default, 0.6),
				justifyContent: leading ? "space-between" : "flex-end",
				flexWrap: "wrap",
				gap: 1,
				...sx,
			}}
		>
			{leading}
			{children}
		</DialogActions>
	);
};

export const MoleCancelButton = (props: ButtonProps) => (
	<Button variant="outlined" color="inherit" {...props} />
);

export const MolePrimaryButton = ({
	color = "primary",
	variant = "contained",
	...props
}: ButtonProps) => <Button color={color} variant={variant} {...props} />;